import useGetPipeline, {
  PipelineStage,
} from '@/api/queries/pipelines/useGetPipeline';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import PipelineStageTargetingAutomations from '@/ui/pages/pipelineConfigPage/components/automations/PipelineStageTargetingAutomations';
import PipelineStageGuards from '@/ui/pages/pipelineConfigPage/components/guards/PipelineStageGuards';
import PipelineConfigHeader from '@/ui/pages/pipelineConfigPage/PipelineConfigHeader';
import PipelineStageConfig from '@/ui/pages/pipelineConfigPage/PipelineStageConfig';
import {
  Button,
  Card,
  Divider,
  DividerWithLabel,
  useToastContext,
} from '@payaca/ambrosia';
import { MAX_PIPELINE_STAGES } from '@payaca/constants/pipeline';
import { SortableList } from '@payaca/dnd';
import { usePrevious } from '@payaca/hooks/usePrevious';
import { validateStageReorder } from '@payaca/shared-isomorphic/pipelines/validateStageReorder';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useDraftReorderPipelineStage from './api/mutations/useDraftReorderPipelineStage';
import PipelineAutomationTriggerModal from './components/modals/PipelineAutomationTriggerModal';
import { DraftModeProvider } from './context/DraftModeContext';
import { PageValidationProvider } from './context/PageValidationContext';

type PipelineConfigPageContentProps = {
  pipelineId: string;
};

const PipelineConfigPageContent: FC<PipelineConfigPageContentProps> = ({
  pipelineId,
}) => {
  /**
   * queries
   */
  const { pipeline, isFetched: hasFetchedPipelineData } =
    useGetPipeline(pipelineId);

  const { pushError } = useToastContext();

  /**
   * mutations - these use the draft QueryClient provided by DraftModeProvider
   */
  const { mutate: reorderStage } = useDraftReorderPipelineStage({
    pipelineId,
  });

  const [
    addAutomationTriggerToPipelineStage,
    setAddAutomationTriggerToPipelineStage,
  ] = useState<string | null>(null);

  const handleDragEnd = useCallback(
    (
      activeItem: PipelineStage,
      destinationIndex: number,
      items: PipelineStage[]
    ) => {
      const stages = pipeline?.stages ?? [];

      const fromIndex = stages.findIndex(
        (s) => s.publicId === activeItem.publicId
      );

      const validation = validateStageReorder(
        stages,
        fromIndex,
        destinationIndex
      );

      if (!validation.valid) {
        pushError(validation.reason);
        return false;
      }

      if (destinationIndex > 0) {
        // Place after the item before it in the new order
        reorderStage({
          id: activeItem.publicId,
          position: { afterStageId: items[destinationIndex - 1].publicId },
        });
      } else if (items.length > 1) {
        // First position - place before the second item
        reorderStage({
          id: activeItem.publicId,
          position: { beforeStageId: items[1].publicId },
        });
      }
    },
    [reorderStage, pipeline, pushError]
  );

  const stages = pipeline?.stages;
  const displayStages = stages ?? [];

  const shouldDisableAddStageActions =
    displayStages.length >= MAX_PIPELINE_STAGES;

  const previousStages = usePrevious(stages);
  const newStageIds = useMemo(() => {
    if (!previousStages || !stages) return new Set<string>();
    const previousIds = new Set(previousStages.map((s) => s.publicId));
    return new Set(
      stages.filter((s) => !previousIds.has(s.publicId)).map((s) => s.publicId)
    );
  }, [stages, previousStages]);

  return (
    <AuthenticatedPageWrapper
      className="pipeline-page-override"
      breadcrumbItems={[
        {
          label: pipeline?.title ?? '',
          to: `/pipelines/${pipeline?.publicId}`,
        },
        { label: 'Manage pipeline' },
      ]}
    >
      <div className="h-full flex flex-col">
        <PipelineConfigHeader
          pipelineId={pipelineId}
          title={pipeline?.title}
          colour={pipeline?.colour}
          isDeletable={pipeline?.isDeletable}
        />

        <div
          className={clstx(
            'flex gap-4 w-full flex-1 p-4',
            hasFetchedPipelineData && 'overflow-x-auto'
          )}
        >
          <SortableList
            orientation="horizontal"
            id="pipeline-config-stages"
            className={'flex gap-4'}
            items={displayStages}
            hasKeyboardSensor={false}
            renderItem={(pipeLineStage) => {
              const index = displayStages.findIndex(
                (s) => s.publicId === pipeLineStage.publicId
              );

              return (
                <PipelineStageConfig
                  key={pipeLineStage.publicId}
                  stageId={pipeLineStage.id}
                  stagePublicId={pipeLineStage.publicId}
                  pipelineId={pipelineId}
                  title={pipeLineStage.title}
                  colour={pipeLineStage.colour}
                  previousStagePublicId={displayStages[index - 1]?.publicId}
                  nextStagePublicId={displayStages[index + 1]?.publicId}
                  shouldDisableAddStageActions={shouldDisableAddStageActions}
                  stageIndex={index}
                  stages={displayStages}
                  isNew={newStageIds.has(pipeLineStage.publicId)}
                >
                  <div className="space-y-2.5">
                    <h4>Projects move here when:</h4>

                    {index === 0 && (
                      <>
                        <Card sizeVariant="sm">
                          <Card.Body>
                            <p>They are created</p>
                          </Card.Body>
                        </Card>
                        <DividerWithLabel label="or" />
                        <Card sizeVariant="sm">
                          <Card.Body>
                            <p>They are transferred from another pipeline</p>
                          </Card.Body>
                        </Card>
                        <DividerWithLabel label="or" />
                      </>
                    )}
                    <Card sizeVariant="sm">
                      <Card.Body>
                        <p>They are manually moved</p>
                      </Card.Body>
                    </Card>

                    <PipelineStageTargetingAutomations
                      pipelinePublicId={pipelineId}
                      pipelineStagePublicId={pipeLineStage.publicId}
                    />

                    <DividerWithLabel label="or" />

                    <Button
                      className="w-full"
                      onClick={() =>
                        setAddAutomationTriggerToPipelineStage(
                          pipeLineStage.publicId
                        )
                      }
                      size="sm"
                      variant="tertiary"
                    >
                      Add trigger
                    </Button>

                    <Divider className="my-4" />

                    <PipelineStageGuards
                      pipelineId={pipelineId}
                      stage={pipeLineStage}
                      stageIndex={index}
                    />
                  </div>
                </PipelineStageConfig>
              );
            }}
            onDragEnd={handleDragEnd}
          />
        </div>
      </div>
      <PipelineAutomationTriggerModal
        pipelinePublicId={pipeline?.publicId}
        pipelineStagePublicId={addAutomationTriggerToPipelineStage ?? undefined}
        pipelineTitle={pipeline?.title}
        isOpen={!!addAutomationTriggerToPipelineStage}
        onClose={() => setAddAutomationTriggerToPipelineStage(null)}
      />
    </AuthenticatedPageWrapper>
  );
};

const PipelineConfigPage: FC = () => {
  const { pipelineId } = useParams<{ pipelineId: string }>();

  if (!pipelineId) {
    return null;
  }

  return (
    <DraftModeProvider pipelineId={pipelineId}>
      <PageValidationProvider>
        <PipelineConfigPageContent pipelineId={pipelineId} />
      </PageValidationProvider>
    </DraftModeProvider>
  );
};

export default PipelineConfigPage;
